/**
 * MesasGridDialogs — New Mesa and QR Code dialogs extracted from MesasGrid.
 */
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, Printer, QrCode, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { TableNfcWriteButton } from "./TableNfcWriteButton";
import { connectQZTray } from "@/utils/printService";
import { toast } from "sonner";

export function NewMesaDialog({ open, onOpenChange, newName, setNewName, newNumber, setNewNumber, newCapacity, setNewCapacity, loading, onCreate }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("tables.newTable")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>{t("tables.tableName")} {t("tables.optional")}</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={t("tables.tableNamePlaceholder")}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("tables.tableNumber")}</Label>
              <Input
                type="number"
                value={newNumber}
                onChange={(e) => setNewNumber(parseInt(e.target.value))}
              />
            </div>
            <div>
              <Label>{t("tables.capacity")}</Label>
              <Input
                type="number"
                value={newCapacity}
                onChange={(e) => setNewCapacity(parseInt(e.target.value))}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button onClick={onCreate} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("tables.createTable")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QRCodeDialog({ showQRModal, onClose, getMenuUrl, downloadQR }) {
  const { t } = useTranslation();
  return (
    <Dialog open={!!showQRModal} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-center">
            {showQRModal?.name || `MESA ${String(showQRModal?.number).padStart(2, "0")}`}
          </DialogTitle>
        </DialogHeader>
        {showQRModal && (
          <div className="flex flex-col items-center py-4">
            <div className="bg-white p-4 rounded-xl shadow-inner">
              <QRCodeSVG
                id={`qr-${showQRModal.id}`}
                value={getMenuUrl(showQRModal)}
                size={200}
                level="H"
              />
            </div>
            <p className="text-sm text-gray-500 mt-4 text-center">
              {t("tables.scanToOrder")}
            </p>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => downloadQR(showQRModal)}>
                <Download className="w-4 h-4 mr-2" /> {t("tables.download")}
              </Button>
              <Button variant="outline" onClick={() => {
                connectQZTray().then(() => {
                  toast.success(t("tables.printing"));
                }).catch(() => toast.error(t("tables.connectPrinter")));
              }}>
                <Printer className="w-4 h-4 mr-2" /> {t("common.print")}
              </Button>
              <TableNfcWriteButton url={getMenuUrl(showQRModal)} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
